// ─── F9 Inkoop — inkooporders + inkoopfacturen ──────────────────────────────
//
// Datamodel: zie packages/db/prisma/schema.prisma — InkoopOrder + InkoopOrderRegel
// + InkoopFactuur + InkoopAudit.
//
// Rekenfuncties (subtotaal/BTW/totaal, state-machine, code-generators) staan
// in packages/utils/src/inkoop-calculations.ts.

import type { InterneGeldstroomRefDTO } from './interne-geldstroom'
import { z } from 'zod'
// batch-59 interne-facturen (FO-432) - samenvoeg-kandidaat op het factuurdetail.
import type { InterneSamenvoegKandidaatDTO } from './interne-facturen'
import { idParam } from './id-validators'
// FO-420 (batch-55) — naamswijziging-blok op het factuur-detail (correctie-mail).
import type { InkoopNaamswijzigingDTO } from './inkoop-correctie-mail'
// batch-68 werkplaatsfacturen (FO-341 §3.1) — herkenning + vaste termijn in de review.
import type {
  AfzenderLeerVoorstelDTO,
  InkoopLeverancierHerkenningDTO,
  InkoopVasteTermijnDTO,
} from './werkplaatsfacturen'
// FO-147 (batch-28) — toewijzingen-array per regel (gedeeld schrijfschema).
import {
  inkoopRegelToewijzingInputSchema,
  refineToewijzingenBinnenRegel,
  KOSTEN_TOEWIJZING_BRON,
  type InkoopRegelToewijzingDTO,
} from './inkoop-toewijzing'

// ─── Enums (matching Prisma enums as string unions) ──────────────────────────

export const INKOOP_ORDER_STATUS = [
  'CONCEPT',
  'GOEDGEKEURD',
  'BESTELD',
  'ONTVANGEN',
  'GEFACTUREERD',
  'GEANNULEERD',
  // Besteltraject-uitbreiding (batch-4 inkoop) — alleen toegevoegd.
  'WACHT_OP_GOEDKEURING',
  'GOEDGEKEURD_VOOR_BESTELLING',
  'IN_BESTELLING',
  'DEELLEVERING_ONTVANGEN',
  'VOLLEDIG_ONTVANGEN',
  'BINNENGEBOEKT',
  'AFGEROND',
  // batch-65 voorraad-besteladvies (FO-481) — automatische order waarvan het
  // bestelbericht niet verstuurd kon worden (mail mislukt): nooit stil.
  'TE_VERSTUREN',
] as const
export type InkoopOrderStatus = (typeof INKOOP_ORDER_STATUS)[number]

// FO-080 (batch-20) — expliciete administratie-statussen. De backoffice mag de
// werk-statussen (ONTVANGEN/IN_BEHANDELING/GOEDGEKEURD/BETWIST) vrij zetten;
// BETAALD/GEANNULEERD zijn terminaal. Mapping van de oude set: OPEN -> ONTVANGEN,
// GEBOEKT -> GOEDGEKEURD (migratie + backfill).
export const INKOOP_FACTUUR_STATUS = [
  // batch-59 interne-facturen (FO-432) - een door een herhaal-regel klaargezet
  // intern concept. Bewust een EIGEN status i.p.v. ONTVANGEN: elke bestaande
  // teller/export/betaallijst filtert op een expliciete statuslijst, dus een
  // klaargezet concept telt daardoor nergens mee tot een mens het bevestigt.
  // Alleen de generator zet 'm; de gewone status-actie kan er niet naartoe
  // (staat niet in INKOOP_FACTUUR_WERK_STATUSSEN).
  'KLAARGEZET',
  'ONTVANGEN',
  'IN_BEHANDELING',
  'GOEDGEKEURD',
  'BETAALD',
  'BETWIST',
  'GEANNULEERD',
  // batch-65 factuur-richting (FO-486) - geen inkoop- maar een verkoopfactuur,
  // als verkoopconcept doorgezet. Niet geannuleerd of afgekeurd; telt nergens
  // mee (expliciete statuslijsten sluiten 'm uit, zie
  // INKOOP_FACTUUR_NIET_MEETELLEND). Alleen de verplaats-actie zet 'm; geen
  // kiesbare status (staat niet in INKOOP_FACTUUR_KIESBARE_STATUSSEN).
  'VERPLAATST_NAAR_VERKOOP',
] as const
export type InkoopFactuurStatus = (typeof INKOOP_FACTUUR_STATUS)[number]

/**
 * batch-65 factuur-richting (FO-486) - de statussen die in GEEN enkele
 * kosten-/crediteuren-teller, export, betaallijst of btw-cijfer meetellen. Een
 * query die "alles behalve geannuleerd" bedoelt, gebruikt `notIn` op deze
 * lijst (of een expliciete positieve lijst), nooit `{ not: 'GEANNULEERD' }`.
 */
export const INKOOP_FACTUUR_NIET_MEETELLEND = [
  'GEANNULEERD',
  'KLAARGEZET',
  'VERPLAATST_NAAR_VERKOOP',
] as const satisfies readonly InkoopFactuurStatus[]

/**
 * batch-59 interne-facturen (FO-432) - de statussen die een MENS mag kiezen (in
 * een status-actie of bij een handmatige import). KLAARGEZET valt er bewust
 * buiten: die zet alleen de herhaal-regel-generator, en de transitietabel kent
 * geen enkele overgang ernaartoe. Gebruik `INKOOP_FACTUUR_STATUS` voor labels en
 * filters (daar hoort KLAARGEZET juist wél in).
 */
// batch-66 inkoop-afwijkingen (FO-490 §2.3): BETWIST staat hier niet meer in.
// "Betwist" volgt uit het afwijkingen-register (open afwijkingen op de
// factuur); de statuswaarde blijft alleen bestaan voor historische rijen.
export const INKOOP_FACTUUR_KIESBARE_STATUSSEN = [
  'ONTVANGEN',
  'IN_BEHANDELING',
  'GOEDGEKEURD',
  'BETAALD',
  'GEANNULEERD',
] as const satisfies readonly InkoopFactuurStatus[]

/**
 * De "werk-statussen" die de backoffice via PATCH /inkoop/facturen/:id/status
 * onderling vrij mag wisselen (geen verplichte goedkeurketen). BETAALD en
 * GEANNULEERD vallen hier bewust buiten: die zijn terminaal.
 */
export const INKOOP_FACTUUR_WERK_STATUSSEN = [
  'ONTVANGEN',
  'IN_BEHANDELING',
  'GOEDGEKEURD',
] as const satisfies readonly InkoopFactuurStatus[]

/**
 * De statussen die "te betalen" zijn (komen mee in een betaallijst / SEPA-run).
 * BETWIST/BETAALD/GEANNULEERD vallen hier nooit onder.
 */
export const INKOOP_FACTUUR_TE_BETALEN_STATUSSEN = [
  'ONTVANGEN',
  'IN_BEHANDELING',
  'GOEDGEKEURD',
] as const satisfies readonly InkoopFactuurStatus[]

/** Bron van een inkoopfactuur (mirror van Prisma enum `InkoopFactuurOrigin`). */
export const INKOOP_FACTUUR_ORIGIN = ['HANDMATIG', 'INKOOPORDER', 'IMPORT'] as const
export type InkoopFactuurOrigin = (typeof INKOOP_FACTUUR_ORIGIN)[number]

export const INKOOP_AUDIT_ACTIE = [
  'AANGEMAAKT',
  'BIJGEWERKT',
  'GOEDGEKEURD',
  'BESTELD',
  'ONTVANGEN',
  'GEFACTUREERD',
  'GEANNULEERD',
  'FACTUUR_AANGEMAAKT',
  'FACTUUR_GEBOEKT',
  'FACTUUR_BETAALD',
  'FACTUUR_BETWIST',
  'FACTUUR_GEANNULEERD',
  'EXPORTED_EXACT',
  // Factuur uit de inkoop-mailbox/PDF ingelezen (concept, ter controle).
  'GEIMPORTEERD',
  // Leverancier (Client type=LEVERANCIER) aangemaakt/gekoppeld vanuit een factuur.
  'LEVERANCIER_AANGEMAAKT',
  // Besteltraject-uitbreiding (batch-4 inkoop).
  'VERZONDEN',
  'GOEDEREN_ONTVANGEN',
  'BINNENGEBOEKT',
] as const
export type InkoopAuditActie = (typeof INKOOP_AUDIT_ACTIE)[number]

// ─── Label constants (NL-talig voor UI) ──────────────────────────────────────

export const INKOOP_ORDER_STATUS_LABELS: Record<InkoopOrderStatus, string> = {
  CONCEPT: 'Concept',
  GOEDGEKEURD: 'Goedgekeurd',
  BESTELD: 'Besteld',
  ONTVANGEN: 'Ontvangen',
  GEFACTUREERD: 'Gefactureerd',
  GEANNULEERD: 'Geannuleerd',
  WACHT_OP_GOEDKEURING: 'Wacht op goedkeuring',
  GOEDGEKEURD_VOOR_BESTELLING: 'Goedgekeurd voor bestelling',
  IN_BESTELLING: 'In bestelling',
  DEELLEVERING_ONTVANGEN: 'Deellevering ontvangen',
  VOLLEDIG_ONTVANGEN: 'Volledig ontvangen',
  BINNENGEBOEKT: 'Binnengeboekt',
  AFGEROND: 'Afgerond',
  TE_VERSTUREN: 'Te versturen',
}

export const INKOOP_ORDER_STATUS_BADGE_VARIANT: Record<
  InkoopOrderStatus,
  'info' | 'warning' | 'success' | 'danger' | 'secondary'
> = {
  CONCEPT: 'secondary',
  GOEDGEKEURD: 'info',
  BESTELD: 'warning',
  ONTVANGEN: 'success',
  GEFACTUREERD: 'success',
  GEANNULEERD: 'danger',
  WACHT_OP_GOEDKEURING: 'warning',
  GOEDGEKEURD_VOOR_BESTELLING: 'info',
  IN_BESTELLING: 'warning',
  DEELLEVERING_ONTVANGEN: 'warning',
  VOLLEDIG_ONTVANGEN: 'success',
  BINNENGEBOEKT: 'success',
  AFGEROND: 'success',
  TE_VERSTUREN: 'danger',
}

export const INKOOP_FACTUUR_STATUS_LABELS: Record<InkoopFactuurStatus, string> = {
  KLAARGEZET: 'Klaargezet',
  ONTVANGEN: 'Ontvangen',
  IN_BEHANDELING: 'In behandeling',
  GOEDGEKEURD: 'Goedgekeurd',
  BETAALD: 'Betaald',
  BETWIST: 'Betwist',
  GEANNULEERD: 'Geannuleerd',
  // batch-65 factuur-richting (FO-486)
  VERPLAATST_NAAR_VERKOOP: 'Geboekt als verkoop',
}

export const INKOOP_FACTUUR_STATUS_BADGE_VARIANT: Record<
  InkoopFactuurStatus,
  'info' | 'warning' | 'success' | 'danger' | 'secondary'
> = {
  KLAARGEZET: 'info',
  ONTVANGEN: 'secondary',
  IN_BEHANDELING: 'warning',
  GOEDGEKEURD: 'info',
  BETAALD: 'success',
  BETWIST: 'danger',
  GEANNULEERD: 'secondary',
  // batch-65 factuur-richting (FO-486)
  VERPLAATST_NAAR_VERKOOP: 'info',
}

// ─── Mailbox-ingest (postvak inkoop@streep.nl) ───────────────────────────────
// Wat er op de inkoop-mailbox binnenkwam en hoe het systeem het verwerkte.

export const INKOOP_MAIL_INGEST_STATUS = [
  'NIEUW',
  'GEEN_BIJLAGE',
  'VERWERKT',
  'TE_CONTROLEREN',
  'FOUT',
  'DUPLICAAT',
  // FO-155 — MKB-brandstoffactuur: doorgestuurd naar de brandstof-import,
  // bewust geen concept-inkoopfactuur.
  'OVERGESLAGEN',
  // Fase F (spec 44) — bijlage door de malware-scan geblokkeerd; alleen een
  // ADMIN kan vrijgeven via de quarantaine-tab in de mail-module.
  'QUARANTAINE',
] as const
export type InkoopMailIngestStatus = (typeof INKOOP_MAIL_INGEST_STATUS)[number]

export const INKOOP_MAIL_INGEST_STATUS_LABELS: Record<InkoopMailIngestStatus, string> = {
  NIEUW: 'Binnengekomen',
  GEEN_BIJLAGE: 'Geen bruikbare bijlage',
  VERWERKT: 'Uitgelezen',
  TE_CONTROLEREN: 'Te controleren',
  FOUT: 'Mislukt',
  DUPLICAAT: 'Duplicaat',
  OVERGESLAGEN: 'Overgeslagen (brandstof)',
  QUARANTAINE: 'Quarantaine (malware-scan)',
}

export const INKOOP_MAIL_INGEST_STATUS_BADGE_VARIANT: Record<
  InkoopMailIngestStatus,
  'info' | 'warning' | 'success' | 'danger' | 'secondary'
> = {
  NIEUW: 'info',
  GEEN_BIJLAGE: 'secondary',
  VERWERKT: 'success',
  TE_CONTROLEREN: 'warning',
  FOUT: 'danger',
  DUPLICAAT: 'secondary',
  OVERGESLAGEN: 'secondary',
  QUARANTAINE: 'danger',
}

export interface InkoopMailIngestRowDTO {
  id: string
  mailbox: string
  afzender: string | null
  onderwerp: string | null
  ontvangenOp: string | null
  status: InkoopMailIngestStatus
  aantalBijlagen: number
  foutmelding: string | null
  verwerktOp: string | null
  aantalFacturen: number
  factuurIds: string[]
  /**
   * batch-65 factuur-richting (FO-486) - facturen uit deze mail die als
   * verkoopfactuur zijn doorgezet (herkomstketen: "Geboekt als verkoop").
   */
  verplaatstNaarVerkoop?: { factuurId: string; verkoopConceptId: string }[]
}

export const inkoopMailIngestListQuerySchema = z.object({
  status: z.enum(INKOOP_MAIL_INGEST_STATUS).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
})

// ─── DTOs voor API responses ─────────────────────────────────────────────────

export interface InkoopLeverancierRefDTO {
  id: string
  name: string
  clientNumber: string
}

export interface InkoopEntityRefDTO {
  id: string
  name: string
}

export interface InkoopOrderRegelDTO {
  id: string
  position: number
  artikel: string
  aantal: number
  prijs: number
  subtotaal: number
  btwPercentage: number | null
}

export interface InkoopOrderListRowDTO {
  id: string
  poNummer: string
  status: InkoopOrderStatus
  leverancier: InkoopLeverancierRefDTO
  omschrijving: string | null
  datum: string // ISO yyyy-MM-dd
  verwachtDatum: string | null
  subtotaal: number
  btwBedrag: number
  totaal: number
  btwPercentage: number
  goedgekeurdDoor: string | null
  goedgekeurdDoorNaam: string | null
  goedgekeurdOp: string | null
  // Compact regel-overzicht voor tabel-tooltip
  regelCount: number
}

export interface InkoopOrderDetailDTO {
  id: string
  poNummer: string
  status: InkoopOrderStatus
  leverancier: InkoopLeverancierRefDTO
  entity: InkoopEntityRefDTO | null
  werkbonId: string | null
  werkbonNumber: string | null
  omschrijving: string | null
  datum: string
  verwachtDatum: string | null
  subtotaal: number
  btwBedrag: number
  totaal: number
  btwPercentage: number
  // State-actor velden
  goedgekeurdDoor: string | null
  goedgekeurdDoorNaam: string | null
  goedgekeurdOp: string | null
  besteldDoor: string | null
  besteldDoorNaam: string | null
  besteldOp: string | null
  ontvangenDoor: string | null
  ontvangenDoorNaam: string | null
  ontvangenOp: string | null
  geannuleerdDoor: string | null
  geannuleerdDoorNaam: string | null
  geannuleerdOp: string | null
  regels: InkoopOrderRegelDTO[]
  audits: InkoopAuditDTO[]
  // FO-393 (batch-46) — compacte 3-way-match-status per gekoppelde factuur.
  matchFacturen: InkoopOrderMatchFactuurDTO[]
  createdAt: string
  updatedAt: string
  createdById: string
  createdByNaam: string | null
}

// FO-393 (batch-46) — één regel voor de match-samenvatting op het orderdetail.
export interface InkoopOrderMatchFactuurDTO {
  factuurId: string
  factuurNr: string
  status: InkoopFactuurStatus
  totaal: number
  matchStatus: InkoopMatchStatus | null
  afwijkingstypes: InkoopMatchRegelOordeel[]
  geaccepteerd: boolean
}

export interface InkoopFactuurListRowDTO {
  id: string
  factuurNr: string
  status: InkoopFactuurStatus
  origin: InkoopFactuurOrigin
  leverancier: InkoopLeverancierRefDTO
  poNummer: string | null
  orderId: string | null
  bedrag: number
  btwBedrag: number
  totaal: number
  factuurdatum: string
  vervaldatum: string | null
  // FO-081 — betaaldatum (ISO yyyy-MM-dd, Europe/Amsterdam) of null; kolom in de lijst.
  betaaldOp: string | null
  boekhoudRef: string | null
  // Mailbox-scan review-velden (gevuld bij origin=IMPORT) voor de
  // "Ingescand / te controleren"-weergave.
  teControleren: boolean
  // batch-9 fa.5 — entityId (BV) naast bvNaam, voor de per-BV boomstructuur-groepering.
  entityId: string | null
  bvNaam: string | null
  extractieZekerheid: number | null
  // True wanneer de gekoppelde leverancier nog een onbevestigde CONCEPT-
  // placeholder is (boeken blijft dan server-side geblokkeerd). Spiegelt het
  // gelijknamige veld op `InkoopFactuurDetailDTO` zodat de lijst "leverancier
  // onbekend" kan markeren zonder per rij het detail te laden.
  leverancierOnbekend: boolean
  // FO-419 (batch-55) — inkoopfactuur van een eigen BV aan deze BV (badge
  // "Intercompany" + filter; telt niet mee in de externe kosten-KPI's).
  isIntercompany: boolean
  // batch-66 inkoop-afwijkingen (FO-490) — de stand uit het afwijkingen-register:
  // het aantal open afwijkingen en of die betalen blokkeren (of de factuur al
  // deels betaald is). Zo ziet wie een betaalrun samenstelt het in de lijst.
  openAfwijkingen: number
  betalenGeblokkeerd: boolean
  // Herreview — een deelbetaling is ook zichtbaar nadat de afwijking is opgelost:
  // wie de factuur daarna op betaald zet moet weten dat alleen het restant nog
  // over te maken is. `restantTeBetalen` = totaal min de deelbetaling (null zonder
  // deelbetaling); `gedektDoorCreditnota` = het deel daarvan dat een gekoppelde
  // creditnota dekt (verrekenen, niet overmaken).
  deelbetaaldBedrag: number | null
  restantTeBetalen: number | null
  gedektDoorCreditnota: number
  // Het nummer van de correctiefactuur die deze factuur vervangt (betalen
  // geblokkeerd, annuleren), of null.
  vervangenDoor: string | null
  intercompanyTegenpartij: { id: string; name: string } | null
  // batch-59 interne-facturen (FO-432) - door een herhaal-regel klaargezet
  // intern concept (status KLAARGEZET). `herhaalPeriode` is de periode-sleutel
  // (`YYYY-MM` / `YYYY-Pnn` / `YYYY-Qn`); null bij elke andere factuur.
  herhaalRegelId: string | null
  herhaalPeriode: string | null
  // batch-50 accountant-rol (FO-404 §B6) — kostentoewijzing samengevat op
  // factuurniveau, zodat lijst én export tonen wie/welke BV de kosten draagt
  // zonder per rij het detail te laden. De BV staat al in `entityId`/`bvNaam`;
  // dit veld voegt de medewerker-kant (FO-147) toe. Gededupliceerd over alle
  // regels van de factuur, gesorteerd op naam.
  toegewezenMedewerkers: InkoopToegewezenMedewerkerDTO[]
  // Aantal toewijzingen op een ánder doeltype (voertuig / gereedschapsdrager).
  // Nul = alle toewijzingen zijn medewerker-toewijzingen; > 0 laat de lijst
  // "+N overig" tonen i.p.v. de indruk te wekken dat er niets toegewezen is.
  toegewezenOverigAantal: number
  // batch-66 interne-geldstroom (FO-494) - de interne geldstroom waar deze
  // (intercompany-)factuur aan hangt; null = nog niet gekoppeld. Optioneel: alleen
  // de lijst-route vult hem (per pagina in één query).
  interneGeldstroom?: InterneGeldstroomRefDTO | null
}

/** batch-50 accountant-rol — één toegewezen medewerker in de inkoop-lijstrij. */
export interface InkoopToegewezenMedewerkerDTO {
  id: string
  naam: string
}

// batch-9 #41 — de oude kostensoort-enum (spiegelt de Prisma-enum). batch-65
// inkoop-kosten (FO-482): alleen nog als LEGACY her-geëxporteerd (de uitgifte-
// boekingen van FO-335 houden hun eigen enum-kolom, en de migratie-mapping leunt
// erop). Nieuwe code gebruikt de stamtabel (`kostensoorten.ts`, `kostensoortId`);
// de drift-test `kostensoort-enum-drift` bewaakt nieuwe verwijzingen.
export { INKOOP_KOSTENSOORT, INKOOP_KOSTENSOORT_LABELS } from './inkoop-kostensoort'
export type { InkoopKostensoort } from './inkoop-kostensoort'
import type { KostensoortBron, KostensoortRefDTO } from './kostensoorten'
// batch-65 inkoop-kosten (FO-483) — doorbelasten naar de verkoopfactuur.
import type { InkoopDoorbelastingDTO, InkoopDoorbelastStatus, NietDoorbelastenReden } from './inkoop-doorbelasten'
import { isKostendragerDoeltype } from './kostendrager'

/** Eén regel van een geïmporteerde inkoopfactuur (wat besteld is). */
export interface InkoopFactuurRegelDTO {
  id: string
  position: number
  omschrijving: string
  aantal: number
  prijsPerStuk: number
  totaal: number
  btwPercentage: number | null
  // Kostensoort blijft PER REGEL (één artikel = één soort); alle toewijzingen
  // van de regel erven hem (FO-147). batch-65 (FO-482): uit de stamtabel.
  kostensoortId: string | null
  /** De gekozen soort (ook als die inmiddels gedeactiveerd is), of null. */
  kostensoortRef: KostensoortRefDTO | null
  /** Waar de soort vandaan kwam ("op basis van eerdere keuze", AI, ...). */
  kostensoortBron: KostensoortBron | null
  /** Zekerheid 0-100 bij een AI-voorstel, anders null. */
  kostensoortZekerheid: number | null
  // FO-147 (batch-28) — meerdere medewerkers per regel met verdeling; vervangt
  // de oude 1-op-1 employeeId/employeeNaam (één waarheid, geen dubbeltelling).
  toewijzingen: InkoopRegelToewijzingDTO[]
  // AI-herkende persoonsnaam op deze regel (hint uit de extractie, ook wanneer
  // de match niet eenduidig was en er dus géén suggestie is).
  aiPersoonsnaam: string | null
  // batch-66 voertuigcontrole (FO-489) — de extractie-regel waar deze regel uit
  // voortkomt (0-based index in de extractie), of null (handmatig toegevoegd,
  // lump-sum, of niet meer terug te vinden). De review-client stuurt hem bij het
  // opslaan ongewijzigd terug: de index hoort voor het leven bij de regel, ook
  // wanneer de regel-id's intussen zijn vervangen (een tweede tabblad).
  rawRegelIndex: number | null
  // batch-62 uitrusting-inkoop (FO-466) — artikelnummer/SKU en maat zoals op de
  // factuur vermeld (vrije tekst; matchen gebeurt canoniek, nooit met ===).
  sku: string | null
  maat: string | null
  // batch-65 inkoop-kosten (FO-483) — doorbelasten naar de verkoopfactuur. null =
  // geen klant-toerekening; de rest volgt uit de toerekening + doorbelastingen.
  doorbelastStatus: InkoopDoorbelastStatus | null
  nietDoorbelastenReden: NietDoorbelastenReden | null
  nietDoorbelastenToelichting: string | null
  /** De doorbelastingen van deze regel (actief en teruggekoppeld), nieuwste eerst. */
  doorbelastingen: InkoopDoorbelastingDTO[]
}

/** Bron-mail van een geïmporteerde inkoopfactuur (inkoop-mailbox). */
export interface InkoopMailIngestRefDTO {
  id: string
  afzender: string | null
  onderwerp: string | null
  ontvangenOp: string | null
}

/**
 * Leverancier-velden zoals de factuur-extractie ze uit de PDF haalde
 * (`InkoopFactuur.rawExtractionJson`). Bedoeld om het "leverancier aanmaken"-
 * paneel in de review voor te vullen wanneer de leverancier nog niet bevestigd
 * is. Alle velden string | null; het hele object is null als er niets parsebaar
 * was.
 */
export interface InkoopLeverancierExtractie {
  naam: string | null
  kvkNummer: string | null
  btwNummer: string | null
  iban: string | null
  email: string | null
  telefoon: string | null
  straat: string | null
  huisnummer: string | null
  postcode: string | null
  plaats: string | null
  land: string | null
}

export interface InkoopFactuurDetailDTO {
  id: string
  factuurNr: string
  status: InkoopFactuurStatus
  origin: InkoopFactuurOrigin
  leverancier: InkoopLeverancierRefDTO
  // BV (STREEP-entiteit) waaronder de factuur valt — null tot in de review gekozen.
  entity: InkoopEntityRefDTO | null
  poNummer: string | null
  orderId: string | null
  bedrag: number
  btwBedrag: number
  totaal: number
  btwPercentage: number | null
  factuurdatum: string
  vervaldatum: string | null
  omschrijving: string | null
  referentienummer: string | null
  boekhoudRef: string | null
  // Mailbox-scan / review-flow (alleen gevuld bij origin=IMPORT).
  teControleren: boolean
  extractieZekerheid: number | null
  // Markeringen uit de AI-extractie + matching (rawExtractionJson.markeringen).
  markeringen: string[]
  // True wanneer de gekoppelde leverancier nog een onbevestigde CONCEPT-
  // placeholder is (boeken blijft dan server-side geblokkeerd).
  leverancierOnbekend: boolean
  // FO-419 (batch-55) — intercompany: de verzendende eigen BV (tegenpartij);
  // de ontvanger is `entity`. Handmatig te zetten/verwijderen met audit.
  isIntercompany: boolean
  intercompanyTegenpartij: { id: string; name: string } | null
  // batch-59 interne-facturen (FO-432) - herhaal-regel-herkomst van een
  // klaargezet concept + de samenvoeg-kandidaat wanneer het klaargezette
  // concept en een mail-exemplaar dezelfde periode dekken (nooit twee boekingen).
  herhaalRegelId: string | null
  herhaalPeriode: string | null
  samenvoegKandidaat: InterneSamenvoegKandidaatDTO | null
  // batch-66 interne-geldstroom (FO-494) - de interne geldstroom waar deze
  // factuur de kostenkant van is, met de wederzijdse verwijzing naar de
  // spiegel-verkoopfactuur; null = (nog) niet gekoppeld. Optioneel: alleen de
  // detail-GET vult hem.
  interneGeldstroom?: InterneGeldstroomRefDTO | null
  // Leverancier-velden uit de extractie om het "leverancier aanmaken"-paneel
  // voor te vullen; null als er niets parsebaar was in rawExtractionJson.
  leverancierExtractie: InkoopLeverancierExtractie | null
  mailIngest: InkoopMailIngestRefDTO | null
  // batch-68 werkplaatsfacturen (FO-341 §3.1) - hoe de leverancier herkend is
  // (afzender / debiteurnummer / PDF), of de vaste termijn van de leverancier de
  // PDF-vervaldatum verving, en het zelflerend voorstel om de mail-afzender aan de
  // gekozen leverancier te koppelen. Optioneel: alleen de detail-GET vult ze.
  leverancierHerkenning?: InkoopLeverancierHerkenningDTO | null
  vasteTermijn?: InkoopVasteTermijnDTO | null
  afzenderLeerVoorstel?: AfzenderLeerVoorstelDTO | null
  // Geïmporteerde regels (wat besteld is) — leeg bij handmatige facturen.
  regels: InkoopFactuurRegelDTO[]
  exactExportedOp: string | null
  geboektDoor: string | null
  geboektDoorNaam: string | null
  geboektOp: string | null
  betaaldDoor: string | null
  betaaldDoorNaam: string | null
  betaaldOp: string | null
  betwistDoor: string | null
  betwistDoorNaam: string | null
  betwistOp: string | null
  betwistReden: string | null
  geannuleerdDoor: string | null
  geannuleerdDoorNaam: string | null
  geannuleerdOp: string | null
  audits: InkoopAuditDTO[]
  // FO-393 (batch-46) — 3-way-match (alleen bij een order-gekoppelde factuur).
  match: InkoopFactuurMatchDTO | null
  // Orderregels (id + artikelnaam) voor de handmatige koppel-UI; leeg zonder order.
  orderRegels: { id: string; artikel: string }[]
  // FO-420 (batch-55) — gevuld wanneer de factuur aan een OUDE naam (alias) van de
  // gekoppelde BV gericht is: dan is de actie "relatie informeren over
  // naamswijziging" beschikbaar. null = factuur op de juiste naam / geen BV.
  naamswijziging: InkoopNaamswijzigingDTO | null
  createdAt: string
  updatedAt: string
  createdById: string
  createdByNaam: string | null
}

// FO-393 (batch-46) — 3-way-match-resultaat voor de review/detail-UI.
export const INKOOP_MATCH_STATUS = ['MATCH_OK', 'MATCH_AFWIJKING'] as const
export type InkoopMatchStatus = (typeof INKOOP_MATCH_STATUS)[number]

export const INKOOP_MATCH_REGEL_OORDEEL = [
  'MATCH_OK',
  'PRIJSAFWIJKING',
  'TEVEEL_GEFACTUREERD',
  'FACTUUR_ZONDER_ONTVANGST',
  'KOPPELING_ONZEKER',
] as const
export type InkoopMatchRegelOordeel = (typeof INKOOP_MATCH_REGEL_OORDEEL)[number]

export const INKOOP_MATCH_REGEL_OORDEEL_LABELS: Record<InkoopMatchRegelOordeel, string> = {
  MATCH_OK: 'Match OK',
  PRIJSAFWIJKING: 'Prijsafwijking',
  TEVEEL_GEFACTUREERD: 'Teveel gefactureerd',
  FACTUUR_ZONDER_ONTVANGST: 'Factuur zonder ontvangst',
  KOPPELING_ONZEKER: 'Koppeling onzeker',
}

export interface InkoopFactuurMatchRegelDTO {
  factuurRegelId: string
  orderRegelId: string | null
  handmatigGekoppeld: boolean
  oordeel: InkoopMatchRegelOordeel
  besteldAantal: number | null
  ontvangenAantal: number | null
  gefactureerdAantal: number
  orderPrijs: number | null
  factuurPrijs: number
  toelichting: string | null
}

export interface InkoopFactuurMatchDTO {
  status: InkoopMatchStatus
  afwijkingstypes: InkoopMatchRegelOordeel[]
  geaccepteerd: boolean
  geaccepteerdDoorNaam: string | null
  geaccepteerdOp: string | null
  geaccepteerdReden: string | null
  laatstGematchtOp: string
  regels: InkoopFactuurMatchRegelDTO[]
}

export interface InkoopAuditDTO {
  id: string
  actie: string
  actor: string
  actorName: string | null
  oudeWaarde: unknown | null
  nieuweWaarde: unknown | null
  toelichting: string | null
  createdAt: string
}

export interface InkoopStatsDTO {
  // KPI's voor orders-tab
  totaalPos: number
  openPosBedrag: number // bedrag van orders met status != GEFACTUREERD/GEANNULEERD
  wachtOpOntvangst: number // aantal orders met status BESTELD
  // KPI's voor facturen-tab (FO-080 statusset). "Open" = nog te behandelen
  // (ONTVANGEN + IN_BEHANDELING); "geboekt" = GOEDGEKEURD (klaar om te betalen).
  totaalOpenFacturen: number // bedrag van facturen met status ONTVANGEN of IN_BEHANDELING
  geboektFacturen: number // bedrag van facturen met status GOEDGEKEURD
  betaaldDitKwartaal: number // bedrag van BETAALD-facturen in lopend kwartaal
  // batch-66 (FO-490): aantal facturen met minstens één open afwijking (het
  // register vervangt de losse status BETWIST).
  betwistAantal: number
  // FO-419 (batch-55) — de factuur-KPI's hierboven sluiten intercompany uit;
  // dit is het intercompany-deel apart (open + geboekt, zelfde statussets).
  intercompanyOpenFacturen: number
  intercompanyGeboektFacturen: number
}

// ─── Zod request-body schemas ────────────────────────────────────────────────

export const inkoopOrderRegelInputSchema = z.object({
  artikel: z.string().trim().min(1).max(500),
  aantal: z.number().nonnegative(),
  prijs: z.number().nonnegative(),
  btwPercentage: z.number().min(0).max(100).optional().nullable(),
  // Review-fix track-C critical #4: optionele FK naar VoorraadArtikel. Zonder
  // dit veld kon de UI/"Bestellen"-knop op /voorraad/alerts geen voorraad-
  // koppeling vastleggen → ONTVANGEN-trigger sloeg de auto-bijboeking over.
  voorraadArtikelId: idParam('voorraadArtikelId').optional().nullable(),
})

export const inkoopOrderCreateSchema = z.object({
  leverancierId: idParam(),
  omschrijving: z.string().trim().max(2000).optional().nullable(),
  datum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  verwachtDatum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().nullable(),
  btwPercentage: z.number().min(0).max(100).default(21),
  entityId: idParam().optional().nullable(),
  werkbonId: idParam().optional().nullable(),
  regels: z.array(inkoopOrderRegelInputSchema).min(1),
})

export const inkoopOrderUpdateSchema = z.object({
  omschrijving: z.string().trim().max(2000).optional().nullable(),
  datum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  verwachtDatum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().nullable(),
  btwPercentage: z.number().min(0).max(100).optional(),
  entityId: idParam().optional().nullable(),
  werkbonId: idParam().optional().nullable(),
  regels: z.array(inkoopOrderRegelInputSchema).optional(),
})

/**
 * Schema voor "werkbon → inkooporder"-flow.
 *
 * Caller geeft een werkbonId + leverancierId; server kopieert alle materialen
 * van de werkbon naar een nieuwe concept-PO. Status start altijd op CONCEPT.
 */
export const inkoopOrderFromWerkbonSchema = z.object({
  werkbonId: idParam(),
  leverancierId: idParam(),
  entityId: idParam().optional().nullable(),
  omschrijving: z.string().trim().max(2000).optional().nullable(),
  btwPercentage: z.number().min(0).max(100).optional(),
})

export type InkoopOrderFromWerkbonInput = z.infer<typeof inkoopOrderFromWerkbonSchema>

export const inkoopOrderTransitionSchema = z.object({
  toelichting: z.string().trim().max(2000).optional(),
  // PR-C2 (voorraad-magazijn spec §6.2) — bij /ontvangen mag de caller
  // optioneel een doel-locatie meegeven waarop de regels worden bijgeboekt.
  // Negeer voor andere transitions (server-side gevalideerd in route).
  voorraadLocatieId: idParam('voorraadLocatieId').optional(),
})

export const inkoopOrderAnnulerenSchema = z.object({
  reden: z.string().trim().min(1).max(2000),
})

export const inkoopOrderListQuerySchema = z.object({
  status: z.enum(INKOOP_ORDER_STATUS).optional(),
  leverancierId: idParam().optional(),
  entityId: idParam().optional(),
  werkbonId: idParam().optional(),
  search: z.string().trim().max(100).optional(),
  page: z.coerce.number().int().positive().default(1),
  // pageSize tot 2000: het backoffice-overzicht haalt de volledige (gefilterde)
  // set in één keer op en sorteert/pagineert client-side (zo werkt de kolom-
  // sortering globaal i.p.v. per server-pagina). Default blijft 20.
  pageSize: z.coerce.number().int().positive().max(2000).default(20),
})

// ─── Factuur-schemas ─────────────────────────────────────────────────────────

export const inkoopFactuurCreateSchema = z.object({
  factuurNr: z.string().trim().min(1).max(50).optional(), // optioneel — server kan nummer genereren
  leverancierId: idParam(),
  orderId: idParam().optional().nullable(),
  // batch-9 #40 — credits/correcties toestaan: negatieve bedragen mogen (de read-DTO
  // staat ze al toe). De DB legt geen restrictie op; de blokkade zat puur in Zod.
  bedrag: z.number(),
  btwBedrag: z.number().default(0),
  factuurdatum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  vervaldatum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().nullable(),
})

/** Eén te-corrigeren regel in de review van een geïmporteerde inkoopfactuur. */
export const inkoopFactuurRegelInputSchema = z
  .object({
    omschrijving: z.string().trim().min(1).max(500),
    // FO-042 — creditnota: negatief aantal toestaan (een creditregel heeft bv.
    // aantal -1). Consistent met de al-signed header-bedragen (batch-9 #40) + de
    // read-DTO; de DB legt geen restrictie op. De blokkade ("Too small: expected
    // number to be >=0") zat puur in deze nonnegative().
    aantal: z.number().default(1),
    prijsPerStuk: z.number(),
    totaal: z.number(),
    btwPercentage: z.number().min(0).max(100).optional().nullable(),
    // batch-65 inkoop-kosten (FO-482) — kostensoort uit de stamtabel.
    kostensoortId: idParam('kostensoortId').optional().nullable(),
    // batch-62 uitrusting-inkoop (FO-466) — SKU + maat reizen mee door de
    // review-PATCH (regels worden daar integraal vervangen). Weggelaten = de
    // waarde van de oorspronkelijke regel blijft staan; null = wissen.
    sku: z.string().trim().max(100).optional().nullable(),
    maat: z.string().trim().max(50).optional().nullable(),
    // FO-147 (batch-28) — verdeling over meerdere medewerkers (aandeel-bedrag
    // excl. btw per medewerker; som <= regelbedrag via de superRefine). De bron
    // reist mee zodat een geaccepteerde suggestie zijn herkomst houdt in de
    // audit; weggelaten = HANDMATIG.
    toewijzingen: z
      .array(
        inkoopRegelToewijzingInputSchema.extend({
          bron: z.enum(KOSTEN_TOEWIJZING_BRON).optional(),
        }),
      )
      .max(50)
      .optional()
      .nullable(),
    // batch-66 voertuigcontrole (FO-489) — het id van de BESTAANDE regel waaruit
    // deze regel voortkomt. De review vervangt de regels integraal; met dit id
    // weet de server exact welke regel bewerkt, verplaatst of nieuw is, zodat de
    // extractie-koppeling (`rawRegelIndex`) en de openstaande voorstellen bij hun
    // eigen regel blijven. `null` = een nieuw toegevoegde regel; weggelaten = een
    // client die het id niet meestuurt (dan koppelt de server voorzichtig op
    // inhoud en bij twijfel niet). Zelfde naam als in de read-DTO. Bewust geen
    // strenge id-validatie: het is een aanwijzing die de server alleen vergelijkt
    // met de regels van de factuur, en een id dat hij niet kent (verouderd
    // tabblad, lege waarde, andere vorm) blokkeert het opslaan niet. Alleen een
    // waarde die geen tekst is of langer dan honderd tekens wordt geweigerd.
    id: z.string().max(100).optional().nullable(),
    // batch-66 voertuigcontrole (FO-489) — de extractie-index zoals de read-DTO
    // hem gaf (`InkoopFactuurRegelDTO.rawRegelIndex`), ongewijzigd terug. De
    // server gebruikt hem alleen wanneer de regel-id's verouderd blijken (een
    // tweede tabblad): de regel is dan exact terug te vinden als de bestaande
    // regel die dezelfde index draagt. Het is een verwijzing, geen opdracht: de
    // index die wordt opgeslagen komt altijd van die bestaande regel.
    rawRegelIndex: z.number().int().min(0).max(100000).optional().nullable(),
  })
  .superRefine((regel, ctx) => {
    refineToewijzingenBinnenRegel(regel, ctx)
    // FO-147 §6 — kostensoort verplicht zodra er een toewijzing is (zonder
    // soort geen definitieve toewijzing). Inline veldfout op het soort-veld.
    // batch-65 (FO-483): alleen bij een INTERNE drager; een klant-toerekening mag
    // zonder soort (telt dan als "zonder kostensoort" in de marge).
    if ((regel.toewijzingen ?? []).some((t) => isKostendragerDoeltype(t.doeltype)) && !regel.kostensoortId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Kies een kostensoort om de toewijzing definitief te maken.',
        path: ['kostensoortId'],
      })
    }
  })

export const inkoopFactuurUpdateSchema = z.object({
  // Correcties in de te-controleren-review: leverancier + BV + bedragen + datums
  // + regels mogen allemaal worden bijgesteld voordat de factuur wordt bevestigd.
  leverancierId: idParam().optional(),
  entityId: idParam().optional().nullable(),
  factuurNr: z.string().trim().min(1).max(50).optional(),
  // batch-9 #40 — credits/correcties toestaan (negatieve bedragen mogen).
  bedrag: z.number().optional(),
  btwBedrag: z.number().optional(),
  btwPercentage: z.number().min(0).max(100).optional().nullable(),
  factuurdatum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  vervaldatum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().nullable(),
  omschrijving: z.string().trim().max(2000).optional().nullable(),
  referentienummer: z.string().trim().max(200).optional().nullable(),
  boekhoudRef: z.string().trim().max(50).optional().nullable(),
  orderId: idParam().optional().nullable(),
  regels: z.array(inkoopFactuurRegelInputSchema).optional(),
})

// FO-052 P3 — "direct als betaald" bij het boeken/bevestigen: een betaaldatum is
// dan VERPLICHT (geen betaald zonder datum). Gedeelde velden + refine zodat de
// API én het form dezelfde regel afdwingen (inline veldfout op `betaaldOp`).
const directBetaaldFields = {
  directBetaald: z.boolean().optional(),
  betaaldOp: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
}
const refineDirectBetaald = (
  d: { directBetaald?: boolean; betaaldOp?: string },
  ctx: z.RefinementCtx,
): void => {
  if (d.directBetaald && !d.betaaldOp) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Een betaaldatum is verplicht om de factuur direct als betaald te markeren.',
      path: ['betaaldOp'],
    })
  }
}

/** Bevestigen & boeken van een te-controleren (geïmporteerde) inkoopfactuur. */
export const inkoopFactuurBevestigenSchema = z
  .object({
    boekhoudRef: z.string().trim().max(50).optional(),
    toelichting: z.string().trim().max(2000).optional(),
    ...directBetaaldFields,
  })
  .superRefine(refineDirectBetaald)

/** Afkeuren van een te-controleren (geïmporteerde) inkoopfactuur. */
export const inkoopFactuurAfkeurenSchema = z.object({
  reden: z.string().trim().min(1).max(2000),
})

/**
 * Leverancier aanmaken/koppelen vanuit een inkoopfactuur (spec
 * inkoopfactuur-leverancier-autoaanmaken Deel 3). De velden komen uit de
 * (eventueel door de gebruiker gecorrigeerde) factuur-extractie. Alleen `naam`
 * is verplicht; al het andere is optioneel/nullable. `verrijkMetKvk` verrijkt
 * ontbrekende velden via de KVK-lookup; `koppelBestaandeClientId` koppelt een
 * expliciet gekozen bestaande relatie i.p.v. dedup/aanmaken.
 */
export const inkoopLeverancierAanmakenSchema = z.object({
  naam: z.string().trim().min(1).max(300),
  kvkNumber: z.string().trim().max(20).optional().nullable(),
  vatNumber: z.string().trim().max(30).optional().nullable(),
  iban: z.string().trim().max(40).optional().nullable(),
  email: z.string().trim().max(320).optional().nullable(),
  phone: z.string().trim().max(40).optional().nullable(),
  street: z.string().trim().max(200).optional().nullable(),
  houseNumber: z.string().trim().max(20).optional().nullable(),
  postalCode: z.string().trim().max(20).optional().nullable(),
  city: z.string().trim().max(120).optional().nullable(),
  country: z.string().trim().max(120).optional().nullable(),
  verrijkMetKvk: z.boolean().default(false),
  koppelBestaandeClientId: idParam().optional(),
})

/**
 * Standalone leverancier aanmaken/koppelen (SPEC A stap 4a) — NIET gebonden aan
 * een factuur. Voedt het handmatige-invoer-formulier en de bulk-import-wizard.
 * Spec Deel 5: INKOOP.canEdit volstaat (er wordt een Client met audit-trail
 * aangemaakt; geen apart CRM-recht nodig). Dezelfde dedup/verrijk/promote-
 * orchestratie als de factuur-gebonden variant, maar zonder placeholder-/
 * factuur-koppeling. Alleen `naam` is verplicht; al het andere optioneel/
 * nullable. `verrijkMetKvk` verrijkt ontbrekende velden via de KVK-lookup;
 * `koppelBestaandeClientId` koppelt een expliciet gekozen bestaande relatie
 * i.p.v. dedup/aanmaken.
 */
export const inkoopLeverancierCreateSchema = z.object({
  naam: z.string().trim().min(1).max(300),
  kvkNumber: z.string().trim().max(20).optional().nullable(),
  vatNumber: z.string().trim().max(30).optional().nullable(),
  iban: z.string().trim().max(40).optional().nullable(),
  email: z.string().trim().max(320).optional().nullable(),
  phone: z.string().trim().max(40).optional().nullable(),
  street: z.string().trim().max(200).optional().nullable(),
  houseNumber: z.string().trim().max(20).optional().nullable(),
  postalCode: z.string().trim().max(20).optional().nullable(),
  city: z.string().trim().max(120).optional().nullable(),
  country: z.string().trim().max(120).optional().nullable(),
  verrijkMetKvk: z.boolean().default(false),
  koppelBestaandeClientId: idParam().optional(),
})

/**
 * Vandaag als kalenderdag (yyyy-MM-dd) in Europe/Amsterdam. `@streep/types` mag
 * niet van `@streep/utils` afhangen (circulair), dus we leiden de NL-dag hier af
 * via `Intl` - geen datum-library. Gebruikt door de betaaldatum-refines
 * (FO-080/FO-081): een betaling die nog niet plaatsvond, boek je niet als betaald.
 */
export function amsterdamVandaagYmd(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Amsterdam',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date())
}

/**
 * Voegt een veld-issue toe wanneer `betaaldOp` (yyyy-MM-dd) ná vandaag
 * (Europe/Amsterdam) ligt. Gedeeld door de status-zet- en markeer-betaald-schemas
 * (FO-080/FO-081), zodat API én form dezelfde "geen toekomst"-regel afdwingen.
 */
export function refineBetaaldOpNietToekomstig(
  betaaldOp: string | undefined,
  ctx: z.RefinementCtx,
  path: (string | number)[] = ['betaaldOp'],
): void {
  if (betaaldOp && betaaldOp > amsterdamVandaagYmd()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'De betaaldatum mag niet in de toekomst liggen.',
      path,
    })
  }
}

/**
 * FO-080 — vrije status-zet-actie (PATCH /inkoop/facturen/:id/status). De
 * backoffice kiest de doelstatus; twee uitzonderingsvelden dwingen extra invoer
 * af: BETAALD vereist een niet-toekomstige `betaaldOp` (FO-052/FO-081). BETWIST
 * is sinds batch-66 (FO-490) geen kiesbare status meer: "betwist" volgt uit het
 * afwijkingen-register en de API geeft voor die waarde een eigen melding.
 * Gedeeld Zod-schema zodat de API (`safeParse`) én het form exact dezelfde
 * regels gebruiken (inline veldfout, geen kale toast).
 */
export const inkoopFactuurStatusZetSchema = z
  .object({
    // FO-432: KLAARGEZET is geen kiesbare doelstatus (alleen de generator zet 'm).
    status: z.enum(INKOOP_FACTUUR_KIESBARE_STATUSSEN),
    betaaldOp: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    reden: z.string().trim().max(2000).optional(),
  })
  .superRefine((d, ctx) => {
    if (d.status === 'BETAALD') {
      if (!d.betaaldOp) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Een betaaldatum is verplicht om de factuur als betaald te markeren.',
          path: ['betaaldOp'],
        })
      } else {
        refineBetaaldOpNietToekomstig(d.betaaldOp, ctx)
      }
    }
  })
export type InkoopFactuurStatusZetInput = z.infer<typeof inkoopFactuurStatusZetSchema>

/**
 * FO-081 — losse "markeer als betaald"-actie. De betaaldatum is verplicht en mag
 * niet in de toekomst liggen (Europe/Amsterdam): een betaling die nog niet
 * plaatsvond, boek je niet als betaald. Gedeeld zodat het form dezelfde regel
 * client-side afdwingt (inline veldfout, geen kale toast).
 */
export const inkoopFactuurMarkBetaaldSchema = z
  .object({
    betaaldOp: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    toelichting: z.string().trim().max(2000).optional(),
    // batch-66 inkoop-afwijkingen (FO-490 §2.3): alleen het onbetwiste deel
    // betalen. Legt het bedrag vast op de factuur; de status blijft staan tot de
    // open afwijkingen zijn opgelost.
    deelbetaling: z.boolean().optional(),
  })
  .superRefine((d, ctx) => refineBetaaldOpNietToekomstig(d.betaaldOp, ctx))

export const inkoopFactuurBoekenSchema = z
  .object({
    boekhoudRef: z.string().trim().max(50).optional(),
    toelichting: z.string().trim().max(2000).optional(),
    ...directBetaaldFields,
  })
  .superRefine(refineDirectBetaald)

export const inkoopFactuurBetwistenSchema = z.object({
  reden: z.string().trim().min(1).max(2000),
})

export const inkoopFactuurAnnulerenSchema = z.object({
  reden: z.string().trim().min(1).max(2000),
})

/** batch-65 factuur-richting (FO-486) - de richting-filters van de inkooplijst. */
export const INKOOP_RICHTING_FILTERS = ['vermoedelijk-verkoop', 'uit-verkoop-postvak'] as const
export type InkoopRichtingFilter = (typeof INKOOP_RICHTING_FILTERS)[number]

export const inkoopFactuurListQuerySchema = z.object({
  status: z.enum(INKOOP_FACTUUR_STATUS).optional(),
  leverancierId: idParam().optional(),
  // batch-9 fa.5 — BV-filter (per-BV boomstructuur). InkoopFactuur.entityId is nullable.
  entityId: idParam().optional(),
  orderId: idParam().optional(),
  // batch-9 #27/#88 — vervaldatum-range voor betaallijsten/overzichten.
  vervaldatumVan: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  vervaldatumTot: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  // batch-14 FO-017 — factuurdatum-range (vlakke lijst + periode-overzicht).
  factuurdatumVan: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  factuurdatumTot: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  // Filter op de mailbox-scan review-state: `true` toont alleen de nog te
  // controleren (geïmporteerde concept-)facturen; `false` verbergt ze.
  // NB: bewust GEEN z.coerce.boolean() — die coerce't "false" óók naar true
  // (elke niet-lege string is truthy). We mappen de query-string expliciet.
  teControleren: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true')),
  // FO-419 (batch-55) — filter op intercompany (`true` = alleen facturen tussen
  // eigen BV's, `false` = zonder). Zelfde expliciete string-mapping als hierboven.
  intercompany: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true')),
  // batch-65 factuur-richting (FO-486) - werkvoorraad "vermoedelijk verkeerde
  // bak": te controleren facturen die met hoge zekerheid verkoop zijn, of die
  // vanuit het verkoop-postvak hierheen verplaatst zijn.
  richting: z.enum(INKOOP_RICHTING_FILTERS).optional(),
  search: z.string().trim().max(100).optional(),
  // batch-65 inkoop-kosten (FO-483 §3.10) — facturen met een regel die aan deze
  // klant (direct, via een werkbon of een project), werkbon of project is
  // toegerekend, en/of met deze doorbelast-status (op dezelfde regel).
  klantId: idParam().optional(),
  werkbonId: idParam().optional(),
  projectId: idParam().optional(),
  doorbelastStatus: z.enum(['NIET', 'TE_DOORBELASTEN', 'DOORBELAST']).optional(),
  page: z.coerce.number().int().positive().default(1),
  // pageSize tot 2000: het backoffice-overzicht haalt de volledige (gefilterde)
  // set in één keer op en sorteert/pagineert client-side (zo werkt de kolom-
  // sortering globaal i.p.v. per server-pagina). Default blijft 20.
  pageSize: z.coerce.number().int().positive().max(2000).default(20),
})

/**
 * FO-414 (batch-57) - review-wachtrij voor de doorklik-review-modus: "welke
 * te-controleren factuur komt na X binnen de ACTUELE filterset". Deelt de
 * filtervelden 1:1 met `inkoopFactuurListQuerySchema` (dezelfde where-builder),
 * zodat de wachtrij exact de zichtbare lijst volgt. Geen paginering: de query
 * levert alleen het volgende id + de openstaande teller.
 *
 * - `naId`   cursor: de zojuist behandelde/bekeken factuur (mag zelf al buiten
 *            de wachtrij liggen - daarom geen Prisma-cursor maar een expliciete
 *            (factuurdatum, id)-vergelijking in de route).
 * - `negeer` ids die de reviewer in deze sessie heeft overgeslagen; die blijven
 *            onaangeroerd maar komen niet opnieuw langs.
 * - `sorteer` review-volgorde; default oudste eerst (spec A1).
 */
export const inkoopReviewWachtrijQuerySchema = inkoopFactuurListQuerySchema
  .omit({ page: true, pageSize: true })
  .extend({
    naId: idParam().optional(),
    negeer: z
      .string()
      .trim()
      .max(6000)
      .optional()
      .transform((v) =>
        v === undefined || v === ''
          ? []
          : v
              .split(',')
              .map((s) => s.trim())
              .filter((s) => s.length > 0)
              // Plafond op de sessielijst: houd de MEEST RECENTE overslaan-acties
              // vast, zodat wat je zojuist oversloeg nooit direct terugkomt.
              .slice(-200),
      ),
    sorteer: z.enum(['oudste', 'nieuwste']).default('oudste'),
  })

export type InkoopReviewWachtrijQuery = z.infer<typeof inkoopReviewWachtrijQuerySchema>

/** FO-414 - antwoord van de review-wachtrij (één lichte server-side query). */
export interface InkoopReviewWachtrijDTO {
  /** Eerstvolgende nog te controleren factuur in de filterset; null = wachtrij leeg. */
  volgendeId: string | null
  volgendeFactuurNr: string | null
  /**
   * Staat de meegegeven `naId` zélf nog te controleren? `null` zonder `naId`.
   * Voedt de collega-race op het besluit: mislukt jouw goedkeuren/afkeuren en is
   * deze factuur intussen uit de wachtrij verdwenen, dan is het geen echte fout
   * maar "Door een collega afgehandeld" - dan springt de flow door i.p.v. een
   * technische statusmelding te tonen.
   */
  naIdOpenstaand: boolean | null
  /**
   * Aantal facturen dat in deze filterset nog te controleren is (exclusief de in
   * deze sessie overgeslagen ids, inclusief de factuur die nu in beeld staat).
   * Voedt de voortgangsteller "3 van 17" en corrigeert zichzelf wanneer een
   * collega meebeoordeelt of er nieuwe scans binnenkomen.
   */
  openTotaal: number
}

export const inkoopFacturenExportSchema = z.object({
  ids: z.array(idParam()).min(1),
})

// ─── Inferred types ──────────────────────────────────────────────────────────

export type InkoopOrderCreateInput = z.infer<typeof inkoopOrderCreateSchema>
export type InkoopOrderUpdateInput = z.infer<typeof inkoopOrderUpdateSchema>
export type InkoopFactuurCreateInput = z.infer<typeof inkoopFactuurCreateSchema>
export type InkoopFactuurUpdateInput = z.infer<typeof inkoopFactuurUpdateSchema>
export type InkoopLeverancierAanmakenInput = z.infer<typeof inkoopLeverancierAanmakenSchema>
export type InkoopLeverancierCreateInput = z.infer<typeof inkoopLeverancierCreateSchema>

// ─── Factuur-import (inkoop) — analyse-response ──────────────────────────────
//
// Zie docs/uploads/factuur-import-inkoop-verkoop.md §6 (leverancier-matching) +
// §7 (PO-koppeling). De analyse-stap matcht de crediteur op een Client met
// type=LEVERANCIER, de ontvangende BV op een Entity en (optioneel) het
// referentienummer op een InkoopOrder. De commit-item-types leven bij de
// handler in apps/api (die `ExtractedInvoiceFields` daar definieert).

export type ImportMatchConfidence = 'high' | 'medium' | 'low' | 'none'

/** Eén geanalyseerde inkoop-rij met leverancier/BV-suggesties + PO-match. */
export interface InkoopImportRow {
  rowKey: string
  source: 'PDF' | 'EXCEL'
  sourceFile?: string
  factuurnummer: string | null
  crediteurNaam: string | null
  crediteurKvk: string | null
  crediteurBtwNummer: string | null
  crediteurIban: string | null
  crediteurEmail: string | null
  crediteurTelefoon: string | null
  crediteurStraat: string | null
  crediteurHuisnummer: string | null
  crediteurPostcode: string | null
  crediteurPlaats: string | null
  crediteurLand: string | null
  bvNaam: string | null
  datum: string | null
  vervaldatum: string | null
  subtotaal: number | null
  btwBedrag: number | null
  btwPercentage: number | null
  totaal: number | null
  referentienummer: string | null
  omschrijving: string | null
  /** Gematchte leverancier (Client met type=LEVERANCIER). */
  leverancier: {
    id: string | null
    name: string | null
    confidence: ImportMatchConfidence
    reasoning: string
  }
  /** Gematchte ontvangende BV (Entity). */
  entity: {
    id: string | null
    name: string | null
    confidence: ImportMatchConfidence
    reasoning: string
  }
  /** Optionele PO-match wanneer referentienummer overeenkomt met een InkoopOrder. */
  poMatch?: { orderId: string; poNummer: string }
  errors: string[]
  warnings: string[]
}

export interface InkoopImportAnalyzeResponse {
  rows: InkoopImportRow[]
  aiEnabled: boolean
  warning?: string
}
